import { useState, useEffect } from "react";
import { QueryLoadError } from "@/components/QueryLoadError";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useContactsPaginated, useCreateContact, useDeleteContact, useSyncFromGhl, useContactStats, type ContactWithStats } from "@/hooks/useContacts";
import { useQuote } from "@/hooks/useQuotes";
import { ContactDetailSheet } from "@/components/contacts/ContactDetailSheet";
import { QuoteDetailPanel } from "@/components/quotes/QuoteDetailPanel";
import { OpenInGhlButton } from "@/components/OpenInGhlButton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Plus, RefreshCw, Trash2, CheckCircle2, XCircle, Users, CloudCog, Clock, DollarSign, ChevronLeft, ChevronRight } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { deriveLoadState, type LoadState } from "@/lib/queryLoadState";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const PAGE_SIZE = 50;

export default function ContactsPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [selectedContact, setSelectedContact] = useState<ContactWithStats | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [viewingQuoteId, setViewingQuoteId] = useState<string | null>(null);
  const { data: viewingQuote } = useQuote(viewingQuoteId);

  // Proper debounce with cleanup
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const contactsQuery = useContactsPaginated(debouncedSearch, page);
  const { data } = contactsQuery;
  const contactsState = deriveLoadState(contactsQuery);
  const contacts = data?.contacts;
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const statsQuery = useContactStats();
  const stats = statsQuery.data;
  const statsState = deriveLoadState(statsQuery);
  const statPlaceholder = statsState.failed ? "—" : <Skeleton className="h-7 w-12 inline-block" />;
  const createContact = useCreateContact();
  const deleteContact = useDeleteContact();
  const syncFromGhl = useSyncFromGhl();

  const handleCreate = () => {
    if (!form.first_name || !form.email) return;
    createContact.mutate(
      { first_name: form.first_name, last_name: form.last_name || undefined, email: form.email, phone: form.phone || undefined },
      {
        onSuccess: () => {
          setCreateOpen(false);
          setForm({ first_name: "", last_name: "", email: "", phone: "" });
        },
      }
    );
  };

  const openDetail = (c: ContactWithStats) => {
    setSelectedContact(c);
    setDetailOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Contacts</h1>
            <p className="text-muted-foreground text-sm">Manage your customer directory, synced with GoHighLevel.</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncFromGhl.mutate()}
              disabled={syncFromGhl.isPending}
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${syncFromGhl.isPending ? "animate-spin" : ""}`} />
              {syncFromGhl.isPending ? "Syncing…" : "Sync from GHL"}
            </Button>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add Contact
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>New Contact</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 pt-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>First Name *</Label>
                      <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                    </div>
                    <div>
                      <Label>Last Name</Label>
                      <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  <Button onClick={handleCreate} disabled={createContact.isPending || !form.first_name || !form.email} className="w-full">
                    {createContact.isPending ? "Creating…" : "Create Contact"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="space-y-3" data-testid="contact-stats">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="flex items-center gap-3 p-4">
                <div className="rounded-lg bg-primary/10 p-2.5">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total ?? statPlaceholder}</p>
                  <p className="text-xs text-muted-foreground">Total Contacts</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-3 p-4">
                <div className="rounded-lg bg-accent/10 p-2.5">
                  <CloudCog className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.synced ?? statPlaceholder}</p>
                  <p className="text-xs text-muted-foreground">Synced with GHL</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-3 p-4">
                <div className="rounded-lg bg-muted p-2.5">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <div className="text-sm font-medium">
                    {!stats
                      ? statPlaceholder
                      : stats.lastSyncAt
                        ? formatDistanceToNow(new Date(stats.lastSyncAt), { addSuffix: true })
                        : "Never"}
                  </div>
                  <p className="text-xs text-muted-foreground">Last GHL Sync</p>
                </div>
              </CardContent>
            </Card>
          </div>
          {statsState.failed && (
            <QueryLoadError
              failedText="Couldn't load contact totals."
              offlineText="You're offline. Contact totals will load when your connection is back."
              state={statsState}
              query={statsQuery}
            />
          )}
        </div>

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search contacts…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          {totalCount > 0 && (
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {totalCount.toLocaleString()} result{totalCount !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-center">Jobs</TableHead>
                <TableHead className="text-right">Total Value</TableHead>
                <TableHead>GHL</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {contactsState.loading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i} data-testid="contacts-loading">
                    {Array.from({ length: 8 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-5 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              {/*
                Above the rows on purpose: a failed refetch keeps the last good page, and a
                notice under 50 rows would be off-screen (#778, same reasoning as #674).
              */}
              {contactsState.failed && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <QueryLoadError
                      failedText="Couldn't load contacts."
                      offlineText="You're offline. Contacts will load when your connection is back."
                      state={contactsState}
                      query={contactsQuery}
                    />
                  </TableCell>
                </TableRow>
              )}
              {!contactsState.failed && contacts?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                    No contacts yet. Add one or sync from GHL.
                  </TableCell>
                </TableRow>
              )}
              {contacts?.map((c) => (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => openDetail(c)}
                  >
                    <TableCell className="font-medium">
                      {c.first_name} {c.last_name || ""}
                    </TableCell>
                    <TableCell className="text-sm">{c.email}</TableCell>
                    <TableCell className="text-sm">{c.phone || "—"}</TableCell>
                    <TableCell className="text-center">
                      {c.job_count > 0 ? (
                        <Badge variant="secondary" className="text-xs">{c.job_count}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {c.total_value > 0 ? (
                        <span className="flex items-center justify-end gap-0.5 font-medium">
                          <DollarSign className="h-3 w-3" />
                          {c.total_value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {c.ghl_contact_id ? (
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                        ) : (
                          <XCircle className="h-4 w-4 text-muted-foreground/40" />
                        )}
                        <span onClick={(e) => e.stopPropagation()}>
                          <OpenInGhlButton
                            ghlContactId={c.ghl_contact_id}
                            ghlLocationId={c.ghl_location_id}
                            className="h-6 w-6"
                          />
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {format(new Date(c.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete contact?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will remove {c.first_name} {c.last_name} from the local database. It will NOT delete them from GHL.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteContact.mutate(c.id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination. A failed page fetch has no data, so totalCount is 0 and this hides;
            the error row's retry, or a new search (which resets to page 0), is the way back. */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page + 1 >= totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <ContactDetailSheet
        contact={selectedContact}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onQuoteClick={(id) => { setDetailOpen(false); setViewingQuoteId(id); }}
      />
      <QuoteDetailPanel
        quote={viewingQuote ?? null}
        open={!!viewingQuoteId}
        onOpenChange={(o) => { if (!o) setViewingQuoteId(null); }}
      />
    </DashboardLayout>
  );
}
